import type { Product, Rubric, SelectedRubric } from "./types";

/**
 * Produits composés (admin, onglet Composition) : `product.composition` = étapes dont chaque choix est un
 * autre produit de la carte, avec un supplément optionnel. Sur le QR, chaque étape est présentée comme
 * une rubrique de la fiche (mêmes règles de choix) ; les options d'un produit choisi se règlent sur sa
 * propre page et suivent son étape dans la ligne (`componentOf`) — même forme que la borne, la tablette
 * et le manager, qui envoie chaque produit choisi vers sa cuisine.
 */

export const hasComposition = (product?: Product | null): boolean =>
  Array.isArray(product?.composition) && product!.composition!.length > 0;

/** Sélection de la fiche : id de rubrique → clé d'élément → quantité. */
export type Selection = Record<string, Record<string, number>>;

export const itemKeyFor = (item: { id?: string }, idx: number): string => item.id ?? `idx-${idx}`;

/**
 * Étapes → rubriques de la fiche. `products` : produits disponibles à cette heure, masqués compris (un
 * produit vendu seulement dans un menu reste proposé) ; absent = non proposé, rupture = grisé.
 */
export const compositionRubrics = (product: Product | null, products: Map<string, Product>): Rubric[] =>
  (product?.composition ?? []).map((step) => ({
    id: `step:${step.id}`,
    stepId: step.id,
    rubricName: step.name,
    rubricName_trans: step.name_trans,
    necessary: !!step.necessary,
    selectMin: Number(step.selectMin) || 0,
    selectMax: Number(step.selectMax) || 1,
    allowSameMultiple: !!step.allowSameMultiple,
    data: (step.items ?? []).flatMap((item) => {
      const p = products.get(item.productId);
      if (!p) return [];
      const supplement = Number(item.supplement) || 0;
      const vat = typeof p.vat === "number" ? p.vat : null;
      return [{
        id: p.id,
        productId: p.id,
        name: p.name,
        name_trans: p.name_trans,
        kitchenName: p.kitchenName ?? "",
        unitPrice: supplement,
        unitPriceHT: vat !== null ? supplement / (1 + vat / 100) : supplement,
        vat,
        canOrder: p.canOrder !== false,
        image: p.image,
      }];
    }),
  }));

/** Options d'un produit choisi : leurs rubriques et ce qui y est coché. Clé : `componentKey`. */
export interface ComponentOptions {
  rubrics: Rubric[];
  selection: Selection;
}
export const componentKey = (rubricId: string, productId: string): string => `${rubricId}|${productId}`;

/**
 * Sélection → options de la ligne : éléments choisis seulement ; une étape porte `stepId` et ses produits
 * leur `productId` ; les options d'un produit choisi suivent son étape (`componentOf`). Les rubriques
 * ordinaires gardent leur forme d'avant.
 */
export const buildSelectedRubrics = (
  rubrics: Rubric[],
  selection: Selection,
  components: Record<string, ComponentOptions> = {},
): SelectedRubric[] => {
  const out: SelectedRubric[] = [];
  for (const r of rubrics) {
    const sel = selection[r.id] || {};
    const chosen = r.data
      .map((it, idx) => ({ it, key: itemKeyFor(it, idx) }))
      .filter(({ key }) => (sel[key] || 0) > 0);
    if (!chosen.length) continue;
    if (!r.stepId) {
      out.push({
        rubricName: r.rubricName,
        data: chosen.map(({ it, key }) => ({
          id: it.id ?? key,
          name: it.name,
          unitPrice: it.unitPrice,
          unitPriceHT: it.unitPriceHT,
          quantity: sel[key]!,
        })),
      });
      continue;
    }
    out.push({
      rubricName: r.rubricName,
      stepId: r.stepId,
      data: chosen.map(({ it, key }) => ({
        id: it.id ?? key,
        productId: it.productId ?? it.id ?? key,
        name: it.name,
        kitchenName: it.kitchenName ?? "",
        unitPrice: it.unitPrice,
        unitPriceHT: it.unitPriceHT ?? it.unitPrice,
        vat: it.vat ?? null,
        quantity: sel[key]!,
      })),
    });
    for (const { it } of chosen) {
      const productId = it.productId ?? it.id;
      const c = productId ? components[componentKey(r.id, productId)] : undefined;
      if (!c) continue;
      for (const option of buildSelectedRubrics(c.rubrics, c.selection)) {
        out.push({ ...option, componentOf: { stepId: r.stepId, productId: productId! }, componentName: it.name });
      }
    }
  }
  return out;
};

/** Produit choisi dans une étape, tel que le panier l'affiche : un rang, ses options à sa suite. */
export interface CompositionRow {
  key: string;
  name: string;
  quantity: number;
  options: string[];
}

/**
 * Options d'une ligne → rangs du panier : un par produit choisi (ordre des étapes), ses options
 * (`componentOf`) rattachées à lui ; les rubriques ordinaires restent à part (`extras`).
 */
export const compositionRows = (options: SelectedRubric[] = []): { rows: CompositionRow[]; extras: string[] } => {
  const rows: CompositionRow[] = [];
  const extras: string[] = [];
  const keyOf = (stepId: string, productId: string): string => `${stepId}|${productId}`;
  for (const r of options) {
    const names = r.data.map((it) => it.name);
    if (r.stepId) {
      for (const it of r.data) {
        rows.push({ key: keyOf(r.stepId, it.productId ?? it.id), name: it.name, quantity: it.quantity || 1, options: [] });
      }
      continue;
    }
    const owner = r.componentOf && rows.find((row) => row.key === keyOf(r.componentOf!.stepId, r.componentOf!.productId));
    (owner ? owner.options : extras).push(...names);
  }
  return { rows, extras };
};
